import { ComponentProps, useCallback, useState } from 'react';
import get from 'lodash/get';
import { useTranslation } from 'react-i18next';

import { OpDataFetchSelect } from 'new-components/DLS/OpDataFetchSelect/OpDataFetchSelect';
import { sortOptionsAlphabetically } from 'utils/sortOptionsAlphabetically';
import { OpCheckbox } from 'new-components/DLS/OpCheckbox/OpCheckbox';
import { OpTooltip } from 'new-components/DLS/OpTooltip/OpTooltip';
import { useIsRestrictedBySiteSpecificRole } from 'utils/customHooks/useIsRestrictedBySiteSpecificRole';
import { useShowSitePartitioningItems } from 'utils/customHooks/useShowSitePartitioningItems';

import './SiteSpecificGroupDataFetchSelectFilter.scss';

type CreateOptionLabel = NonNullable<
  ComponentProps<typeof OpDataFetchSelect>['createOptionLabel']
>;

type CreateOptionLabelProps = Parameters<CreateOptionLabel>;

interface SiteSpecificGroupDataFetchSelectFilterProps {
  defaultValue?: string[];
  onChange: (value: string[]) => void;
  orgId: number;
  pathToValue?: string;
  filterSort?: ComponentProps<typeof OpDataFetchSelect>['filterSort'];
}

// Container for the dropdown render of the antd select
const Dropdown: ComponentProps<typeof OpDataFetchSelect>['popupRender'] = (
  options,
) => {
  return (
    <div className="site-specific-group-data-fetch-select-filter-dropdown">
      {options}
    </div>
  );
};

// Container for the tag render of the antd select
const Tag: ComponentProps<typeof OpDataFetchSelect>['tagRender'] = ({
  label,
}) => {
  return (
    <div className="site-specific-group-data-fetch-select-filter__tag">
      {label}
    </div>
  );
};

/**
 * SiteSpecificGroupDataFetchSelectFilter
 * This filter's purpose is to gracefully handle large data sets for filterable options for the table,
 * specifically for groups with site-specific access limitations. It allows for searching the options
 * and pulling in more than Heliums limit as needed.
 * @param defaultValue - The default filter value.
 * @param onChange - The onChange handler for the dataFetchSelect
 * @param orgId - The organization ID used to fetch group data.
 * @param pathToValue - The path to the value property of the record (from the data set returned by the query options). The table component uses this value to search the data of the table. Defaults to 'id'.
 * @param filterSort - The sort function to sort the options - passed to dataFetchSelect
 *
 * @returns A React component that renders a site-specific group filter.
 */
export const SiteSpecificGroupDataFetchSelectFilter = ({
  defaultValue,
  onChange,
  orgId,
  pathToValue = 'id',
  filterSort = sortOptionsAlphabetically,
}: SiteSpecificGroupDataFetchSelectFilterProps) => {
  const { t } = useTranslation();
  const [selectValue, setSelectValue] = useState<string[]>(defaultValue || []);
  const checkSiteSpecificRoleRestriction =
    useIsRestrictedBySiteSpecificRole(orgId);
  const { isShowingSitePartitioningItems } =
    useShowSitePartitioningItems(orgId);

  const handleChange = useCallback(
    (value: (string | number)[]) => {
      const stringValues = value.map(String);
      setSelectValue(stringValues);
      onChange(stringValues);
    },
    [onChange],
  );

  // Need to use createOptionLabel vs OptionRender so that the antd select onclick event fires when clicking anywhere in the option including the checkbox
  const createOptionLabel = useCallback(
    (record: CreateOptionLabelProps[0]) => {
      const label = get(record, 'name');
      const optionValue = get(record, pathToValue);
      const groupSites = get(record, 'sites');

      const isSelected = selectValue.map(String).includes(String(optionValue));

      const isRestricted = checkSiteSpecificRoleRestriction(
        groupSites?.map(({ id: siteId }: { id: number }) => siteId),
      );

      const tooltip = isRestricted
        ? t('Your site-specific role prevents management of this group')
        : null;

      return (
        <div className="site-specific-group-data-fetch-select-filter-dropdown__option">
          <OpTooltip title={tooltip}>
            <OpCheckbox
              className="site-specific-group-data-fetch-select-filter-dropdown__checkbox"
              checked={isSelected}
              disabled={isRestricted}
            >
              {label}
            </OpCheckbox>
          </OpTooltip>
        </div>
      );
    },
    [checkSiteSpecificRoleRestriction, pathToValue, selectValue, t],
  );

  return (
    <OpDataFetchSelect
      className="site-specific-group-data-fetch-select-filter"
      size="small"
      testId="table-filter-site-specific-group-data-fetch-select"
      createOptionLabel={createOptionLabel}
      fetchAllInitialValues={{
        /**
         * Pass withSites and ignoreSiteSpecificAccess to fetch all groups so that we can create options for
         * any groups that may have been restricted by site-specific access. Creating the options
         * allows us to display the group name as well as disabling the option if the group has
         * site-specific restrictions.
         */
        ...(isShowingSitePartitioningItems && {
          queryParamOverrides: {
            options: 'withSites,ignoreSiteSpecificAccess',
          },
        }),
      }}
      createDisabledOption={({ sites }) =>
        checkSiteSpecificRoleRestriction(sites?.map(({ id }) => id))
      }
      placeholder={t('Filter')}
      pathToValue={pathToValue}
      pathToLabel="name"
      onChange={handleChange}
      maxTagCount={0}
      maxTagPlaceholder={t('{{selectedCount}} selected', {
        selectedCount: selectValue.length,
      })}
      mode="multiple"
      value={selectValue}
      allowClear
      queryOptions={{
        apiEndpointName: 'listGroups',
        parameters: isShowingSitePartitioningItems
          ? [orgId, { options: 'withSites' }]
          : [orgId],
      }}
      filterSort={filterSort}
      tagRender={Tag}
      popupRender={Dropdown}
      styles={{
        popup: {
          root: {
            width: 'fit-content',
          },
        },
      }}
    />
  );
};
